import { Chart as ChartJS } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useEffect, useMemo, useRef, useState } from "react";
import { Bar } from "react-chartjs-2";
import "./RatingChart.css"; // for styling
import { useIntl } from "react-intl";

import { ImpactCategory } from "../../../__generated__/globalTypes";
import {
  RatingDistributionByCategory,
  SalesDashboardData,
} from "../../../data-store";
import {
  allImpactRatings,
  ImpactRating,
  impactRatingToLongName,
  impactRatingToLetter,
} from "../../../domain/impactRatings";
import { addOpacityToColor } from "../../../util/addOpacityToColor";
import {
  blackCol,
  chartGridLineCol,
  highD,
  lowB,
  mediumC,
  midGreyCol,
  veryHighE,
  veryLowA,
  whiteCol,
} from "../../graphs/colors";
import { externalTooltipHandler } from "../../graphs/utils/TooltipUtils";
import { ImpactRatingInfo } from "../../scope-3/ImpactRatingProportionsChart";
import { ratingColors } from "../../utils/ratingColors";
import TooltipOverlay from "../../utils/TooltipOverlay";
import { useImpactCategory } from "../ImpactCategoryProvider";

const DATA_MAP: {
  [key in ImpactCategory]: "ghg" | "waterUse" | "landUse";
} = {
  GHG: "ghg",
  WATER_USE: "waterUse",
  LAND_USE: "landUse",
};

const ratingToId = (rating: ImpactRating): string => {
  switch (rating) {
    case "VLOW":
      return "veryLow";
    case "LOW":
      return "low";
    case "MEDIUM":
      return "medium";
    case "HIGH":
      return "high";
    case "VHIGH":
      return "veryHigh";
  }
};

function getTooltipRangeLabel(
  rating: ImpactRating,
  thresholds: ImpactRatingInfo["impactRatingInfo"],
  unit: string
): string {
  const currentIdx = thresholds.findIndex((t) => t.impactRating === rating);
  const current = thresholds[currentIdx];
  const next = thresholds[currentIdx + 1];

  const format = (val: number) =>
    Intl.NumberFormat(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(val);

  // Case 1: Very low (no lower bound)
  if (current.lowerBound === null && next?.lowerBound != null) {
    return `<${format(next.lowerBound)} ${unit}`;
  }

  // Case 2: Very high (last item)
  if (currentIdx === thresholds.length - 1) {
    return `>${format(current.lowerBound!)} ${unit}`;
  }

  // Case 3: Normal range
  const lower = format(current.lowerBound!);
  const upper = format(next.lowerBound!);
  return `${lower} - ${upper} ${unit}`;
}

const RatingDistributionChart = ({
  data,
  impactRating,
}: {
  data: SalesDashboardData;
  impactRating: ImpactRatingInfo;
}) => {
  const intl = useIntl();
  const { impactCategory } = useImpactCategory();

  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [salesProductsHovered, setSalesProductsHovered] = useState<
    null | "sales" | "products"
  >(null);
  const salesProductsHoveredRef = useRef<"sales" | "products" | null>(null);
  const chartRef = useRef<ChartJS>(null);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    const datasetSales = chart.data.datasets[0];
    const datasetProducts = chart.data.datasets[1];

    const opacityFn = (index: number) =>
      hoveredIndex === null || hoveredIndex === index ? 1 : 0.3;

    if (salesProductsHovered === "sales") {
      datasetProducts.backgroundColor = [
        veryLowA,
        lowB,
        mediumC,
        highD,
        veryHighE,
      ].map((color, index) => addOpacityToColor(color, 0));
      datasetSales.backgroundColor = [
        veryLowA,
        lowB,
        mediumC,
        highD,
        veryHighE,
      ].map((color, index) => addOpacityToColor(color, opacityFn(index)));
    } else if (salesProductsHovered === "products") {
      datasetSales.backgroundColor = [
        veryLowA,
        lowB,
        mediumC,
        highD,
        veryHighE,
      ].map((color, index) => addOpacityToColor(color, 0));
      datasetProducts.backgroundColor = [
        veryLowA,
        lowB,
        mediumC,
        highD,
        veryHighE,
      ].map((color, index) => addOpacityToColor(color, opacityFn(index)));
    } else {
      datasetSales.backgroundColor = [
        veryLowA,
        lowB,
        mediumC,
        highD,
        veryHighE,
      ].map((color, index) => addOpacityToColor(color, opacityFn(index)));
      datasetProducts.backgroundColor = [
        veryLowA,
        lowB,
        mediumC,
        highD,
        veryHighE,
      ].map((color, index) => addOpacityToColor(color, opacityFn(index)));
    }

    chart.update("default"); // trigger update with animation
  }, [salesProductsHovered, hoveredIndex]);

  useEffect(() => {
    salesProductsHoveredRef.current = salesProductsHovered;
  }, [salesProductsHovered]);

  const labels = useMemo(
    () =>
      allImpactRatings.map((rating) => ({
        title: impactRatingToLongName(rating, intl),
        id: ratingToId(rating),
        rating,
      })),
    [intl]
  );

  const badges = useMemo(
    () => allImpactRatings.map((r) => impactRatingToLetter(r, intl)),
    [intl]
  );

  const chartData = useMemo(() => {
    const salesData = labels.map((label) => {
      const categoryData = data.salesRatings?.[DATA_MAP[impactCategory]];
      if (!categoryData) return "0";
      return (
        categoryData[
          label.id as keyof RatingDistributionByCategory
        ]?.percentage.toLocaleString(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }) ?? 0
      );
    });

    const productsData = labels.map((label) => {
      const categoryData = data.productsRatings?.[DATA_MAP[impactCategory]];
      if (!categoryData) return "0";
      return (
        categoryData[
          label.id as keyof RatingDistributionByCategory
        ]?.percentage.toLocaleString(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }) ?? "0"
      );
    });

    const barColor = [veryLowA, lowB, mediumC, highD, veryHighE].map(
      (col, index) => {
        const color = col;
        const isActive = hoveredIndex === null || hoveredIndex === index;
        return addOpacityToColor(color, isActive ? 1 : 0.3);
      }
    );

    return {
      labels: labels.map((label) => label.title),
      datasets: [
        {
          label: intl.formatMessage({
            id: "components/pages:SalesDashBoard/RatingDistributionBarChart/sales",
            defaultMessage: "Sales",
          }),
          data: salesData,
          backgroundColor:
            salesProductsHoveredRef.current === "products"
              ? "transparent"
              : barColor,
          hoverBackgroundColor:
            salesProductsHoveredRef.current === "products"
              ? "transparent"
              : barColor,
        },
        {
          label: intl.formatMessage({
            id: "components/pages:SalesDashBoard/RatingDistributionBarChart/products",
            defaultMessage: "Products",
          }),
          data: productsData,
          backgroundColor:
            salesProductsHoveredRef.current === "sales"
              ? "transparent"
              : barColor,
          hoverBackgroundColor:
            salesProductsHoveredRef.current === "sales"
              ? "transparent"
              : barColor,
        },
      ],
    };
  }, [
    data.productsRatings,
    data.salesRatings,
    hoveredIndex,
    impactCategory,
    intl,
    labels,
  ]);

  const customTooltip = (tooltip: any) => {
    const sale = (data as any).salesRatings[DATA_MAP[impactCategory]][
      labels[tooltip.dataPoints[0].dataIndex].id
    ];
    const product = (data as any).productsRatings[DATA_MAP[impactCategory]][
      labels[tooltip.dataPoints[0].dataIndex].id
    ];
    return (
      <Tooltip
        label={labels[tooltip.dataPoints[0].dataIndex]}
        sale={sale}
        product={product}
        impactCategory={impactCategory}
        intl={intl}
      />
    );
  };

  const options = {
    indexAxis: "y" as const,
    responsive: true,
    maintainAspectRatio: false,
    borderRadius: 4,
    barPercentage: 0.8,
    maxBarThickness: 26,
    animation: {
      duration: 300, // general animation time
    },
    onHover: (_: any, elements: any[]) => {
      if (elements.length > 0) {
        setHoveredIndex(elements[0].index);
      } else {
        setHoveredIndex(null);
      }
    },
    datasets: {
      bar: {
        backgroundColor: {
          type: "color",
          duration: 300,
          easing: "easeOutQuad",
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        anchor: "end",
        align: "start",
        formatter: (value: string) => {
          if (value === "0") return "";
          return `${value}%`;
        },
        color: (e: any) => {
          const datasetIndex = e.datasetIndex;
          const dataIndex = e.dataIndex;

          const isSales = datasetIndex === 0;
          const isProducts = datasetIndex === 1;

          const labelColor =
            dataIndex === 1 || dataIndex === 2 ? blackCol : whiteCol;

          // Hide labels if the group is hovered off
          if (
            (salesProductsHovered === "sales" && isProducts) ||
            (salesProductsHovered === "products" && isSales)
          ) {
            return "transparent";
          }

          if (hoveredIndex === null) {
            return labelColor;
          }
          return addOpacityToColor(
            labelColor,
            hoveredIndex === dataIndex ? 1 : 0.3
          );
        },
        font: {
          size: 13,
          weight: "500",
        },
      },
      tooltip: {
        enabled: false,
        mode: "index",
        intersect: true,
        external: (context: { tooltip: any; chart: any }) => {
          const dataset = context?.tooltip?.dataPoints?.[0]?.dataset as any;
          if (dataset?.temp) return;

          if (!context.tooltip || !context.chart) return;

          if (!dataset || dataset?.temp) return;

          if (!context.tooltip || !context.chart) return;

          return externalTooltipHandler(
            context,
            true,
            customTooltip,
            undefined,
            300,
            true
          );
        },
      },
    },
    scales: {
      y: {
        display: false, // hide axis labels. We’ll render custom ones in HTML
      },
      x: {
        border: {
          display: false,
        },
        grid: {
          color: chartGridLineCol,
        },
        beginAtZero: true,
        ticks: {
          callback: (val: number | string) => `${val}%`,
          stepSize: 10,
          color: midGreyCol,
          font: {
            size: 13,
          },
        },
      },
    },
  };

  const UNIT = useMemo(() => {
    switch (impactCategory) {
      case "GHG":
        return intl.formatMessage({
          id: "util/units:kgco2eperkg",
          defaultMessage: `kg CO₂e / kg`,
        });
      case "LAND_USE":
        return intl.formatMessage({
          defaultMessage: "m²year / kg",
          id: "util/units:m2yearperkg",
        });
      case "WATER_USE":
        return intl.formatMessage({
          defaultMessage: "L / kg",
          id: "util/units:Lperkg",
        });
      default:
        return intl.formatMessage({
          id: "util/units:unknownUnit",
          defaultMessage: "Unknown Unit",
        });
    }
  }, [impactCategory, intl]);

  return (
    <div className="RatingDistributionBarChart_container">
      <h4 className="RatingDistributionBarChart_title">
        {intl.formatMessage({
          id: "components/pages:SalesDashBoard/RatingDistributionBarChart/title",
          defaultMessage: "Rating Distribution of Sales & Products",
        })}
      </h4>
      <div className="rating-chart-wrapper">
        <div
          className="chart-labels"
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          {labels.map((label, i) => (
            <div
              className="d-flex"
              style={{
                justifyContent: "end",
                gap: 8,
              }}
              key={label.id}
            >
              <LabelWithTooltip
                label={label}
                badge={badges[i]}
                overlay={getTooltipRangeLabel(
                  label.rating,
                  impactRating.impactRatingInfo,
                  UNIT
                )}
                hoveredIndex={
                  hoveredIndex != null ? labels[hoveredIndex].id : null
                }
              />
              <div className="d-flex" style={{ flexDirection: "column" }}>
                <div
                  className={`sub-label ${
                    salesProductsHovered === "products" ||
                    (hoveredIndex != null && hoveredIndex !== i)
                      ? "sub-label-deactive"
                      : ""
                  }`}
                  onMouseEnter={() => {
                    setHoveredIndex(null);
                    setSalesProductsHovered("sales");
                  }}
                  onMouseLeave={() => {
                    setSalesProductsHovered(null);
                  }}
                >
                  {intl.formatMessage({
                    id: "components/pages:SalesDashBoard/RatingDistributionBarChart/sales",
                    defaultMessage: "Sales",
                  })}
                </div>
                <div
                  className={`sub-label ${
                    salesProductsHovered === "sales" ||
                    (hoveredIndex != null && hoveredIndex !== i)
                      ? "sub-label-deactive"
                      : ""
                  }`}
                  onMouseEnter={() => {
                    setHoveredIndex(null);
                    setSalesProductsHovered("products");
                  }}
                  onMouseLeave={() => {
                    setSalesProductsHovered(null);
                  }}
                >
                  {intl.formatMessage({
                    id: "components/pages:SalesDashBoard/RatingDistributionBarChart/products",
                    defaultMessage: "Products",
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="RatingDistributionBarChart_chart-container">
          <Bar
            ref={chartRef as any}
            data={chartData}
            options={options as any}
            height={labels.length * 65}
            width={"100%"}
            plugins={[ChartDataLabels]}
          />
        </div>
      </div>
    </div>
  );
};

export default RatingDistributionChart;

interface Props {
  label: { title: string; id: string };
  badge: string;
  overlay: React.ReactNode;
  hoveredIndex?: string | null;
}

export const LabelWithTooltip = ({
  label,
  badge,
  overlay,
  hoveredIndex,
}: Props) => {
  const labelRef = useRef<HTMLSpanElement | null>(null);
  const [offset, setOffset] = useState(0);
  const GAP = 8; // Gap value for display flex container

  useEffect(() => {
    if (labelRef.current) {
      const width = labelRef.current.getBoundingClientRect().width;
      setOffset((width + GAP) / 2); // Shift tooltip to align with badge
    }
  }, [label.title]);

  return (
    <TooltipOverlay
      spanStyle={{ display: "flex", alignItems: "center" }}
      id={`rating-label-${label.id}`}
      placement="top"
      overlay={overlay}
      popperConfig={
        {
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [offset, 0],
              },
            },
          ],
        } as any
      }
      tooltipClassName="SalesDashboardPage_RatingBarChart_label-tooltip_tooltip-shifted"
    >
      <div
        className="group-label"
        style={{
          gridRow: `span 2`,
          justifyContent: "end",
          opacity: hoveredIndex === null || hoveredIndex === label.id ? 1 : 0.3,
        }}
      >
        <span
          ref={labelRef}
          className="SalesDashboardPage_RatingBarChart_label-rating"
        >
          {label.title}
        </span>
        <span className={`badge badge-${badge}`}>{badge}</span>
      </div>
    </TooltipOverlay>
  );
};

interface TooltipProps {
  label: { id: string; title: string; rating: ImpactRating };
  sale: {
    value: number;
    percentage: number;
  };
  product: {
    value: number;
    percentage: number;
  };
  impactCategory: string;
  intl: ReturnType<typeof useIntl>;
}

export function Tooltip(props: TooltipProps) {
  const PRODUCTS_TITLE = useMemo(() => {
    switch (props.label.rating) {
      case "VLOW":
        return props.intl.formatMessage({
          id: "components/pages:SalesDashBoard/RatingDistributionBarChart/tooltip/productsTitleVLOW",
          defaultMessage: "Very low rated products",
        });
      case "LOW":
        return props.intl.formatMessage({
          id: "components/pages:SalesDashBoard/RatingDistributionBarChart/tooltip/productsTitleLOW",
          defaultMessage: "Low rated products",
        });
      case "MEDIUM":
        return props.intl.formatMessage({
          id: "components/pages:SalesDashBoard/RatingDistributionBarChart/tooltip/productsTitleMEDIUM",
          defaultMessage: "Medium rated products",
        });
      case "HIGH":
        return props.intl.formatMessage({
          id: "components/pages:SalesDashBoard/RatingDistributionBarChart/tooltip/productsTitleHIGH",
          defaultMessage: "High rated products",
        });
      case "VHIGH":
        return props.intl.formatMessage({
          id: "components/pages:SalesDashBoard/RatingDistributionBarChart/tooltip/productsTitleVHIGH",
          defaultMessage: "Very high rated products",
        });
      default:
        return "Unknown rating";
    }
  }, [props.label.rating, props.intl]);

  const HIGH_RATED_SALES = useMemo(() => {
    switch (props.label.rating) {
      case "VLOW":
        return props.intl.formatMessage({
          id: "components/pages:SalesDashBoard/RatingDistributionBarChart/tooltip/highRatedSalesVLOW",
          defaultMessage: "Very low rated sales",
        });
      case "LOW":
        return props.intl.formatMessage({
          id: "components/pages:SalesDashBoard/RatingDistributionBarChart/tooltip/highRatedSalesLOW",
          defaultMessage: "Low rated sales",
        });
      case "MEDIUM":
        return props.intl.formatMessage({
          id: "components/pages:SalesDashBoard/RatingDistributionBarChart/tooltip/highRatedSalesMEDIUM",
          defaultMessage: "Medium rated sales",
        });
      case "HIGH":
        return props.intl.formatMessage({
          id: "components/pages:SalesDashBoard/RatingDistributionBarChart/tooltip/highRatedSalesHIGH",
          defaultMessage: "High rated sales",
        });
      case "VHIGH":
        return props.intl.formatMessage({
          id: "components/pages:SalesDashBoard/RatingDistributionBarChart/tooltip/highRatedSalesVHIGH",
          defaultMessage: "Very high rated sales",
        });
      default:
        return "Unknown rating";
    }
  }, [props.label.rating, props.intl]);
  return (
    <div
      style={{ background: "black", borderRadius: 10, padding: 8 }}
      className="ImpactByRating_Tooltip"
    >
      <div>
        <div
          style={{
            alignItems: "center",
            justifyContent: "space-between",
          }}
          className="d-flex"
        >
          <div className="body-copy-medium">{HIGH_RATED_SALES}</div>
          <div
            style={{
              background: ratingColors[props.label.id],
              width: 10,
              height: 10,
              borderRadius: "100%",
            }}
          ></div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ minWidth: 67 }}>
            <div className="body-copy-medium">
              {props.intl.formatNumber(props.sale.value)}
            </div>
            <div className="small-graph-legends sub-label-color">
              {props.intl.formatMessage({
                id: "components/pages:SalesDashBoard/RatingDistributionBarChart/tooltip/sales",
                defaultMessage: "sales",
              })}
            </div>
          </div>
          <div style={{ minWidth: 67 }}>
            <div className="body-copy-medium">
              {props.intl.formatNumber(props.sale.percentage / 100, {
                style: "percent",
                maximumFractionDigits: 2,
              })}
            </div>
            <div className="small-graph-legends sub-label-color">
              {props.intl.formatMessage({
                id: "components/pages:SalesDashBoard/RatingDistributionBarChart/tooltip/ofTotal",
                defaultMessage: "of total",
              })}
            </div>
          </div>
        </div>
      </div>
      <div className="SalesChart_Tooltip_divider" />
      <div>
        <div
          style={{
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
          }}
          className="d-flex"
        >
          <div className="body-copy-medium">{PRODUCTS_TITLE}</div>
          <div
            style={{
              background: ratingColors[props.label.id],
              width: 10,
              height: 10,
              borderRadius: "100%",
            }}
          ></div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ minWidth: 67 }}>
            <div className="body-copy-medium">
              {props.intl.formatNumber(props.product.value)}
            </div>
            <div className="small-graph-legends sub-label-color">
              {props.intl.formatMessage({
                id: "components/pages:SalesDashBoard/RatingDistributionBarChart/tooltip/sales",
                defaultMessage: "sales",
              })}
            </div>
          </div>
          <div style={{ minWidth: 67 }}>
            <div className="body-copy-medium">
              {props.intl.formatNumber(props.product.percentage / 100, {
                style: "percent",
                maximumFractionDigits: 2,
              })}
            </div>
            <div className="small-graph-legends sub-label-color">
              {props.intl.formatMessage({
                id: "components/pages:SalesDashBoard/RatingDistributionBarChart/tooltip/ofTotal",
                defaultMessage: "of total",
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
